// MemberCard.js - Electronic Business Card Page
import React, { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import { doc, getDoc } from 'firebase/firestore';
import { db } from '../firebaseConfig';
import kosLogo from '../assets/kos_logo_cropped.svg';
import './MemberCard.css';

const MemberCard = () => {
  const { userId } = useParams();
  const [memberData, setMemberData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    const fetchMemberData = async () => {
      try {
        if (!userId) {
          setError('Invalid member ID');
          setLoading(false);
          return;
        }

        const userDocRef = doc(db, 'users', userId);
        const userDoc = await getDoc(userDocRef);

        if (userDoc.exists()) {
          setMemberData({ id: userDoc.id, ...userDoc.data() });
        } else {
          setError('Member not found');
        }
      } catch (err) {
        console.error('Error fetching member data:', err);
        setError('Failed to load member information');
      } finally {
        setLoading(false);
      }
    };

    fetchMemberData();
  }, [userId]);

  if (loading) {
    return (
      <div className="member-card-page">
        <div className="loading-container">
          <div className="spinner"></div>
          <p>Loading member card...</p>
        </div>
      </div>
    );
  }

  if (error || !memberData) {
    return (
      <div className="member-card-page">
        <div className="error-container">
          <div className="error-icon">⚠️</div>
          <h2>Error</h2>
          <p>{error || 'Member not found'}</p>
        </div>
      </div>
    );
  }

  const fullName = `${memberData.title || ''} ${memberData.firstName || ''} ${memberData.lastName || ''}`.trim();
  const avatarUrl = memberData.photoURL || null;
  const shortName = fullName || 'Member';
  const membershipId = memberData.membershipID || 'N/A';
  const statusLabel = memberData.isActive !== false ? 'Active' : 'Inactive';
  const statusClass = memberData.isActive !== false ? 'active' : 'inactive';
  const todayLabel = new Date().toLocaleDateString(undefined, {
    year: 'numeric',
    month: 'short',
    day: '2-digit',
  });

  return (
    <div className="member-card-page">
      <div className="e-card" aria-label="KOS Member Electronic Card">
        <div className="e-card-header">
          <img src={kosLogo} alt="Kuwait Ophthalmic Society" className="e-card-logo" />
          <div className="e-card-org">
            <div className="e-card-org-name">Kuwait Ophthalmic Society</div>
            <div className="e-card-org-subtitle">Member Electronic Card</div>
          </div>
          <div className="e-card-status-wrap">
            <span className={`e-card-status ${statusClass}`}>{statusLabel}</span>
            <div className="e-card-status-date">{todayLabel}</div>
          </div>
        </div>

        <div className="e-card-body">
          <div className="e-card-avatar" aria-hidden={!avatarUrl}>
            {avatarUrl ? (
              <img src={avatarUrl} alt={shortName} className="e-card-avatar-img" />
            ) : (
              <div className="e-card-avatar-fallback" aria-label="Member initials">
                {memberData.firstName?.charAt(0) || 'U'}{memberData.lastName?.charAt(0) || ''}
              </div>
            )}
          </div>

          <div className="e-card-main">
            <div className="e-card-name">{shortName}</div>
            {(memberData.designation || memberData.specialty) && (
              <div className="e-card-role">
                {[memberData.designation, memberData.specialty].filter(Boolean).join(' • ')}
              </div>
            )}
            {memberData.workplace && <div className="e-card-workplace">{memberData.workplace}</div>}

            <div className="e-card-meta">
              <div className="e-card-meta-item">
                <div className="e-card-meta-label">Membership ID</div>
                <div className="e-card-meta-value">{membershipId}</div>
              </div>
            </div>
          </div>
        </div>

        <div className="e-card-footer">
          <span>KOS 2025 2027</span>
        </div>
      </div>
    </div>
  );
};

export default MemberCard;
